/**
 * Utilitaires d'optimisation d'images pour améliorer les Core Web Vitals
 * - WebP/AVIF avec fallback
 * - Responsive srcset
 * - Lazy loading intelligent
 * - Compression adaptative
 */

// Détection du support WebP/AVIF par le navigateur
let webpSupport: boolean | null = null;
let avifSupport: boolean | null = null;

export const checkWebPSupport = async (): Promise<boolean> => {
  if (webpSupport !== null) return webpSupport;
  
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      webpSupport = webP.height === 2;
      resolve(webpSupport);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

export const checkAVIFSupport = async (): Promise<boolean> => {
  if (avifSupport !== null) return avifSupport;
  
  return new Promise((resolve) => {
    const avif = new Image();
    avif.onload = avif.onerror = () => {
      avifSupport = avif.height === 2;
      resolve(avifSupport);
    };
    avif.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKCBgABogQEDQgMgkQAAAAB8dSLfI=';
  });
};

// Optimiser une URL d'image Supabase avec les transformations appropriées
export const optimizeSupabaseImageUrl = (
  url: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'webp' | 'avif' | 'origin';
  } = {}
): string => {
  if (!url || !url.includes('supabase.co')) return url;
  
  const { width, height, quality = 80, format = 'webp' } = options;
  const transformations: string[] = [];
  
  if (width) transformations.push(`width=${width}`);
  if (height) transformations.push(`height=${height}`);
  if (quality) transformations.push(`quality=${quality}`);
  if (format !== 'origin') transformations.push(`format=${format}`);
  
  const separator = url.includes('?') ? '&' : '?';
  return transformations.length > 0 
    ? `${url}${separator}${transformations.join('&')}`
    : url;
};

// Générer un srcset responsive pour une image
export const generateResponsiveSrcset = (
  baseUrl: string,
  widths: number[] = [320, 640, 960, 1280, 1920]
): string => {
  if (!baseUrl.includes('supabase.co')) return '';
  
  return widths
    .map(width => `${optimizeSupabaseImageUrl(baseUrl, { width, quality: 80 })} ${width}w`)
    .join(', ');
};

// Générer l'attribut sizes optimal basé sur le viewport
export const generateSizesAttribute = (
  breakpoints: { maxWidth: string; size: string }[] = [
    { maxWidth: '640px', size: '100vw' },
    { maxWidth: '1024px', size: '50vw' },
    { maxWidth: '1280px', size: '33vw' }
  ],
  defaultSize: string = '25vw'
): string => {
  const sizeQueries = breakpoints
    .map(bp => `(max-width: ${bp.maxWidth}) ${bp.size}`)
    .join(', ');
  
  return `${sizeQueries}, ${defaultSize}`;
};

// Déterminer si une image doit être lazy-loaded selon sa position
export const shouldLazyLoad = (element: HTMLElement | null): boolean => {
  if (!element) return true;
  
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  
  // Ne pas lazy-load si l'image est dans le viewport ou proche (2x viewport)
  return rect.top > viewportHeight * 2;
};

// Calculer les dimensions optimales pour une image
export const calculateOptimalDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight?: number
): { width: number; height: number } => {
  const aspectRatio = originalWidth / originalHeight;
  
  let width = Math.min(originalWidth, maxWidth);
  let height = maxHeight ? Math.min(originalHeight, maxHeight) : width / aspectRatio;
  
  if (maxHeight && height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return {
    width: Math.round(width),
    height: Math.round(height)
  };
};

// Preload d'une image critique pour améliorer le LCP
export const preloadCriticalImage = (
  url: string,
  options: {
    as?: 'image';
    type?: string;
    crossOrigin?: 'anonymous' | 'use-credentials';
  } = {}
): void => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = url;
  link.as = options.as || 'image';
  if (options.type) link.type = options.type;
  if (options.crossOrigin) link.crossOrigin = options.crossOrigin;
  
  // Utiliser setAttribute pour fetchpriority (pas de prop React ici)
  link.setAttribute('fetchpriority', 'high');
  
  document.head.appendChild(link);
};

// Adapter la qualité d'image selon la connexion
export const getQualityByConnection = (): number => {
  if (!('connection' in navigator)) return 80;
  
  const connection = (navigator as any).connection;
  const effectiveType = connection?.effectiveType;
  
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 50;
    case '3g':
      return 65;
    case '4g':
    default:
      return 80;
  }
};

// Compresser une image côté client avant upload
export const compressImageBeforeUpload = async (
  file: File,
  maxWidth: number = 1920,
  quality: number = 0.85
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        
        const { width, height } = calculateOptimalDimensions(
          img.width,
          img.height,
          maxWidth
        );
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
